import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, Bike, Zap, Layers, List, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => iso ? iso.split('-').reverse().join('/') : '';
const fmtEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
const STATUS_LABEL = { activa: 'Activa', en_taller: 'En taller', prestada: 'Prestada' };

function Stat({ label, value, sub, color, icon: Icon, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`text-2xl font-bold mt-1 font-['Barlow_Condensed'] ${color}`} data-testid={testId}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      <Icon size={20} className="text-slate-300" />
    </div>
  );
}

export default function FleetHistory({ isAdmin }) {
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBikes, setShowBikes] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const consult = async () => {
    if (!date) { toast.error('Selecciona una fecha'); return; }
    setLoading(true);
    setShowBikes(false);
    try {
      const res = await axios.get(`${API}/stats/fleet-history`, { params: { date } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al consultar el histórico de flota');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="fleet-history-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <History size={18} className="text-orange-500" /> Histórico de flota
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="h-8 w-40" data-testid="fleet-history-date" />
            <Button size="sm" onClick={consult} disabled={loading} className="bg-orange-500 hover:bg-orange-600 h-8" data-testid="fleet-history-btn">
              {loading ? 'Consultando…' : 'Consultar'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">Reconstruye la flota activa (activas, en taller y prestadas) según el historial real de cada bicicleta: alta, cambios de estado, venta y baja.</p>
      </CardHeader>
      {data && (
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800" data-testid="fleet-history-title">Flota activa a {fmtDate(data.date)}</h3>
            <Button size="sm" variant={showBikes ? 'default' : 'outline'} onClick={() => setShowBikes(!showBikes)} className="h-8 gap-1" data-testid="fleet-history-toggle-bikes">
              {showBikes ? <X size={14} /> : <List size={14} />} {showBikes ? 'Ocultar bicicletas' : 'Ver bicicletas'}
            </Button>
          </div>
          <div className={`grid grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
            <Stat label="Flota activa" value={data.total_active_fleet} sub={data.loaned_count > 0 ? `Incluye ${data.loaned_count} prestada${data.loaned_count === 1 ? '' : 's'}` : null} color="text-blue-600" icon={Bike} testId="fleet-history-total" />
            <Stat label="eBikes" value={data.electric_count} color="text-orange-600" icon={Zap} testId="fleet-history-electric" />
            <Stat label="Normales" value={data.normal_count} color="text-slate-800" icon={Bike} testId="fleet-history-normal" />
            {isAdmin && <Stat label="Valor flota activa" value={fmtEur(data.total_fleet_value)} color="text-blue-700" icon={Layers} testId="fleet-history-value" />}
          </div>
          {showBikes && (
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
              <Table data-testid="fleet-history-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Nº</TableHead>
                    <TableHead className="text-xs uppercase">Modelo</TableHead>
                    <TableHead className="text-xs uppercase">Talla</TableHead>
                    <TableHead className="text-xs uppercase">Tipo</TableHead>
                    <TableHead className="text-xs uppercase">Estado en fecha</TableHead>
                    <TableHead className="text-xs uppercase">Estado actual</TableHead>
                    {isAdmin && <TableHead className="text-xs uppercase text-right">P. compra</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.bicycles.length === 0 && (
                    <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-slate-400 py-6">No había bicicletas activas en esa fecha</TableCell></TableRow>
                  )}
                  {data.bicycles.map((b) => (
                    <TableRow key={b.id} data-testid={`fleet-history-row-${b.id}`}>
                      <TableCell className="font-semibold">{b.number || b.internal_code || '—'}</TableCell>
                      <TableCell>{b.model}</TableCell>
                      <TableCell>{b.size}</TableCell>
                      <TableCell>{b.is_electric ? <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px]">eBike</Badge> : <span className="text-xs text-slate-500">Normal</span>}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{STATUS_LABEL[b.status_at_date] || b.status_at_date}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {b.deleted ? <span className="flex items-center gap-1 text-red-600"><Trash2 size={12} /> Dada de baja</span> : (b.current_status || '—')}
                      </TableCell>
                      {isAdmin && <TableCell className="text-right">{fmtEur(b.purchase_price)}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
